/**
 * @namespace models
 */

/**
 * Type class
 */

/**
 * Define custom type for models
 *
 * @class TypeFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.Type} class
 *
 * @ngdoc factory
 * @name mwApp.factory:TypeFactory
 */
/* @ngInject */
export default function TypeFactory ($injector) {
  /**
   * Type class <br>
   * Can be defined in model in order to support intricate cast logic
   *
   * @example
   *   ...
   *   date: Type({
   *       // define cast to model value
   *       from: function(s, cache) {
   *           return new Date(s);
   *       },
   *       // define cast to server value
   *       to: function(d) {
   *           return +d;
   *       }
   *   })
   *   ...
   *
   * @class Type
   * @memberof models
   * @see models
   * @return {models.Type}
   */
  function Type (p) {
    // function called as usual function, without 'new' keyword
    // -> look at parameter and decide, what should return
    if (!(this instanceof Type)) {
      switch (typeof p) {
        // Type('...'), Type(String('...'))
        case 'string':
          return new Type({
            value: p,
            from: String,
            to: String
          })

        // Type({...})
        case 'object':
          return new Type(p)

        // otherwise, just return parameter itself
        default:
          return p
      }

    // function called as constructor, with 'new' keyword
    // -> create new Type object
    } else {
      Object.assign(this, p)
    }
  }
  Type.prototype = {

    /**
     * Default value
     *
     * @memberof models.Type#
     * @type {*}
     */
    value: null,

    /**
     * Cast value from server to custom value, saved in model
     *
     * @memberof models.Type#
     * @param  {*} v - value from server
     * @return {*} value in model
     */
    from: function (v) {
      return v
    },

    /**
     * Cast value in model to custom value, required by server
     *
     * @memberof models.Type#
     * @param  {*} v - value in model
     * @return {*} value to server
     */
    to: function (v) {
      return v
    },

    /**
     * Get default value from object, using 'from' method
     *
     * @memberof models.Type#
     * @return {*} default value
     */
    getDefault: function () {
      return this.from(this.value)
    }

  }

  /* === Static common types === */

  /**
   * Comma separated string, converted to array
   * e.g.
   *   '1;2;3' <=> Type.CSV(';') <=> ['1','2','3']
   *   'a,b,c' <=> Type.CSV(',') <=> ['a','b','c']
   *
   * @memberof models.Type#
   * @param {String} separator
   * @return {Object}
   */
  Type.CSV = (separator) =>
    new Type({
      from: (s) =>
        Array.isArray(s) || !s ? s : String(s).split(separator),
      to: (a) =>
        Array.isArray(a) ? a.join(separator) : a
    })

  /**
   * Stringified JSON -> to Object
   *
   * @memberof models.Type#
   * @return {Object}
   */
  Type.JSON = new Type({
    from: function (s) {
      return s ? JSON.parse(String(s)) : s
    },
    to: function (o) {
      return JSON.stringify(o)
    }
  })

  /**
   * Return stump of an object, used in Type.Stump
   *
   * @private
   * @memberof models.Type
   * @param  {Object} object
   * @param  {String[]} fields
   * @return {Object}
   */
  function getStump (object, fields) {
    // if it is reference to another entity -> return number
    if (typeof object === 'number') {
      return object
    }

    var ret = Object.create(null)
    for (var i = fields.length; i--;) {
      ret[fields[i]] = object[fields[i]]
    }
    return ret
  }

  /**
   * Stump of a Entity,
   * get real entities from stumps, but sent stumps on server
   *
   * @memberof models.Type#
   * @return {Object}
   */
  Type.Stump = function (cast, fields) {
    var Entity = $injector.get('Entity')

    return new Type({
      from: function (what, cache) {
        return Entity.cast(what, cast, cache)
      },
      to: function (what, cache) {
        var o = Entity.uncast(what, cast, cache)
        var ret

        if (Array.isArray(o)) {
          ret = []
          for (var i = 0; i < o.length; i++) {
            ret.push(getStump(o[i], fields))
          }
        } else {
          ret = getStump(o, fields)
        }

        return ret
      }
    })
  }

  return Type
}
